import type { EventDetailsDto, EventMarketDto, EventMarketLineDto, EventOddDto } from '@/api/queries';
import { displaySpecifierValue, sanitizeOutcomeDisplay } from '@/i18n/outcome-labels';
import { parseApiUtcDate } from '@/lib/date';
import { getTournamentOrCategoryBadge } from '@/shell/sports-menu-utils';
import type { OddsState } from '@/ui/common/odds-button';
import type { EventOutcome } from '@/ui/sports/EventRow';
import type { LiveClockState } from './live-clock';
import type { LiveStatistics } from './live-statistics';
import { parseLiveStatistics, applyLiveStatisticsPatch, resolveEventRowLiveInfo, type LiveEventStatisticsPatch } from './live-statistics';
import { mergeOddsPatches, outcomeFlashKey } from './live-odds';
import type { OddsFlash } from '@/ui/common/odds-button';
import { parseMarketsString } from './event-row';
import { compareMarketGroups } from './markets';
import { formatOddForDisplay } from './odds-format';

/** Virtual first tab on the event page — shows every market, unfiltered. */
export const POPULAR_MARKET_GROUP = 'Popular';

export interface EventDetailsHeader {
  league: string;
  category?: string | null;
  categoryId?: number;
  tournament?: string | null;
  tournamentId?: number;
  crestSrc?: string;
  home: string;
  away: string;
  type: 'prematch' | 'live';
  /** Prematch kick-off in a long weekday format. */
  kickoff?: string;
  /** Raw event date for locale-aware kickoff formatting in the UI. */
  date?: string | null;
  /** Live clock, e.g. `67' · 2HT`. */
  clock?: string;
  liveClockState?: LiveClockState;
  homeScore?: string;
  awayScore?: string;
}

export interface EventDetailsLine {
  specifier?: string;
  main: boolean;
  outcomes: EventOutcome[];
}

export interface EventDetailsMarket {
  id: string;
  key: string;
  group: string;
  lines: EventDetailsLine[];
}

export interface EventDetailsGroupTab {
  id: string;
  code: string;
}

export interface EventDetailsViewModel {
  eventId: number;
  header: EventDetailsHeader;
  tabs: EventDetailsGroupTab[];
  markets: EventDetailsMarket[];
  statistics?: LiveStatistics | null;
  matchTrackerEnabled: boolean;
  theSportsId?: string | null;
  sport?: string | null;
  /** When true, this event has a live stream offer (still gated by account config). */
  liveStream: boolean;
  /** Prematch Bet Builder offer flag from SportsAPI. */
  betBuilderAvailable?: boolean;
}

/** Long kick-off label matching the Figma match header, e.g. `Saturday, 1 August 2026 18:00`. */
export function formatEventKickoff(
  dateValue: string | null | undefined,
  locale?: string,
): string | undefined {
  if (!dateValue) return undefined;
  const date = parseApiUtcDate(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;

  const resolvedLocale = locale || undefined;
  const weekday = new Intl.DateTimeFormat(resolvedLocale, { weekday: 'long' }).format(date);
  const dayMonthYear = new Intl.DateTimeFormat(resolvedLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
  const time = new Intl.DateTimeFormat(resolvedLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);

  return `${weekday}, ${dayMonthYear} ${time}`;
}

export function formatOutcomeLabel(outcome: string, specifier?: string): string {
  const base = sanitizeOutcomeDisplay(outcome);
  const displaySpecifier = displaySpecifierValue(specifier);
  if (!displaySpecifier) return base;
  // Ticket OutcomeName often already embeds the line (e.g. "Over 2.5").
  if (base.includes(displaySpecifier)) return base;
  return sanitizeOutcomeDisplay(`${base} ${displaySpecifier}`);
}

function normalizeMarketCode(value: string): string {
  return value
    .trim()
    .replace(/^br:/i, '')
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
}

/** Next Goal lines carry a goal-number specifier that should not appear on the button. */
export function isNextGoalMarket(
  marketKey: string,
  marketGroup?: string,
  marketLabel?: string,
): boolean {
  const codes = [marketKey, marketGroup ?? ''].map(normalizeMarketCode);
  if (codes.some((code) => code === 'NXG' || code === 'NG' || code === 'NEXTGOAL')) {
    return true;
  }

  return Boolean(marketLabel && /next\s*goal/i.test(marketLabel.trim()));
}

function pickMainLine(lines: EventMarketLineDto[]): EventMarketLineDto | undefined {
  if (!lines.length) return undefined;
  return lines.find((line) => line.Main === true) ?? lines[0];
}

function oddState(eventActive: boolean, marketActive: boolean, lineActive: boolean, odd: EventOddDto | undefined): OddsState {
  const value = odd?.Value ?? 0;
  const locked = !eventActive || !marketActive || !lineActive || !odd || !odd.Active || value <= 0;
  return locked ? 'locked' : 'default';
}

function mapLineOutcomes(
  market: EventMarketDto,
  line: EventMarketLineDto,
  eventActive: boolean,
  flashContext?: {
    eventId: number;
    getOutcomeFlash?: (flashKey: string) => OddsFlash | undefined;
  },
): EventOutcome[] {
  const specifier = line.Specifier?.trim() || undefined;
  const marketActive = market.Active !== false;
  const lineActive = line.Active !== false;

  return (line.Odds ?? []).map((odd) => {
    const value = odd.Value ?? 0;
    const flashKey =
      flashContext?.getOutcomeFlash && flashContext.eventId
        ? outcomeFlashKey(flashContext.eventId, market.Key, specifier, odd.Outcome)
        : undefined;
    const flash = flashKey ? flashContext?.getOutcomeFlash?.(flashKey) : undefined;

    return {
      id: `${market.Key}:${specifier ?? ''}:${odd.Outcome}`,
      // API outcome code only (UOF: 1/2/3, Standard: 1/X/2). Never substitute
      // header display glyphs here — OutcomeTranslates keys collide (br:2 = draw).
      outcome: odd.Outcome,
      outcomeCode: odd.Outcome,
      odds: formatOddForDisplay(value),
      state: oddState(eventActive, marketActive, lineActive, odd),
      flash,
      specifier,
      marketId: market.Key,
      marketName: market.Key,
      oddValue: value > 0 ? value : undefined,
    };
  });
}

function mapMarket(
  market: EventMarketDto,
  index: number,
  eventActive: boolean,
  flashContext?: {
    eventId: number;
    getOutcomeFlash?: (flashKey: string) => OddsFlash | undefined;
  },
): EventDetailsMarket | null {
  const rawLines = market.Lines ?? [];
  if (rawLines.length === 0) return null;

  const mainLine = pickMainLine(rawLines);
  const lines: EventDetailsLine[] = [];

  for (const line of rawLines) {
    const outcomes = mapLineOutcomes(market, line, eventActive, flashContext);
    if (outcomes.length === 0) continue;
    lines.push({
      specifier: line.Specifier?.trim() || undefined,
      main: line === mainLine,
      outcomes,
    });
  }

  if (lines.length === 0) return null;

  // Keep the main line first so the card body matches the Figma "Alternative Options" split.
  lines.sort((a, b) => Number(b.main) - Number(a.main));

  return {
    id: `${market.Id ?? market.Key}-${index}`,
    key: market.Key,
    group: market.Group ?? market.Key,
    lines,
  };
}

function uniqueGroupCodes(markets: EventDetailsMarket[]): string[] {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const market of markets) {
    const code = market.group;
    const key = code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    codes.push(code);
  }
  return codes;
}

function isPopularCode(code: string): boolean {
  return code.trim().toLowerCase() === POPULAR_MARKET_GROUP.toLowerCase();
}

function buildTabs(dto: EventDetailsDto, markets: EventDetailsMarket[]): EventDetailsGroupTab[] {
  const fromApi = (dto.MarketGroups ?? []).map((code) => code.trim()).filter(Boolean);
  const codes = fromApi.length > 0 ? fromApi : uniqueGroupCodes(markets);
  const rest = codes.filter((code) => !isPopularCode(code));

  return [
    { id: POPULAR_MARKET_GROUP, code: POPULAR_MARKET_GROUP },
    ...rest.map((code) => ({ id: code, code })),
  ];
}

export function filterEventMarkets(
  markets: EventDetailsMarket[],
  groupId: string,
): EventDetailsMarket[] {
  if (isPopularCode(groupId)) return markets;
  const needle = groupId.trim().toLowerCase();
  return markets.filter((market) => market.group.toLowerCase() === needle);
}

export function mapEventDetails(
  dto: EventDetailsDto,
  options?: {
    translatePeriod?: (key: string) => string;
    statisticsPatch?: LiveEventStatisticsPatch;
    oddsPatch?: EventMarketDto[];
    getOutcomeFlash?: (flashKey: string) => OddsFlash | undefined;
    eventMetaPatch?: { active?: boolean; marketsCount?: number; expireOdds?: string };
  },
): EventDetailsViewModel {
  const patchedDto = applyLiveStatisticsPatch(dto, options?.statisticsPatch);
  const snapshot = parseMarketsString(patchedDto.MarketsString) ?? [];
  const parsed = options?.oddsPatch?.length
    ? mergeOddsPatches(snapshot, options.oddsPatch)
    : snapshot;
  const live = dto.Live;
  const liveInfo = live
    ? resolveEventRowLiveInfo(patchedDto, options?.translatePeriod)
    : null;
  const sorted = [...parsed].sort((a, b) =>
    compareMarketGroups(a.Group ?? a.Key, b.Group ?? b.Key, live),
  );

  const eventActive = options?.eventMetaPatch?.active ?? dto.Active;
  const flashContext = {
    eventId: dto.EventId,
    getOutcomeFlash: options?.getOutcomeFlash,
  };

  const markets = sorted
    .map((market, index) => mapMarket(market, index, eventActive, flashContext))
    .filter((market): market is EventDetailsMarket => market !== null);

  const crestSrc = getTournamentOrCategoryBadge(dto.Tournament, dto.Category);

  return {
    eventId: dto.EventId,
    header: {
      league: [dto.Category, dto.Tournament].filter(Boolean).join(' > '),
      category: dto.Category,
      categoryId: dto.CategoryId,
      tournament: dto.Tournament,
      tournamentId: dto.TournamentId,
      crestSrc,
      home: dto.Home ?? 'Home',
      away: dto.Away ?? 'Away',
      type: live ? 'live' : 'prematch',
      kickoff: live ? undefined : formatEventKickoff(dto.Date),
      date: live ? undefined : dto.Date,
      clock: live ? liveInfo?.clock : undefined,
      liveClockState: live ? liveInfo?.liveClockState : undefined,
      homeScore: live ? liveInfo?.homeScore : undefined,
      awayScore: live ? liveInfo?.awayScore : undefined,
    },
    tabs: buildTabs(dto, markets),
    markets,
    statistics: live ? parseLiveStatistics(patchedDto.Statistics) : null,
    matchTrackerEnabled: dto.MatchTrackerEnabled,
    theSportsId: dto.TheSportsId,
    sport: dto.Sport,
    liveStream: dto.LiveStream === true,
    betBuilderAvailable: dto.BetBuilderAvailable === true,
  };
}
